import React, { useMemo, useEffect, useState } from "react";
import "./Sidebar.css";
import "./CurrentCategorySidebar.css";
import { Typography } from "@mui/material";

// CurrentCategorySidebar: shows all entries with the same level, style, and size, and the user's mark (or dash if none)
function CurrentCategorySidebar({ scoreData = [], rowIndex = 1, user, judgeResponses, onSelectEntry, colIndexes, judgesColIndexes }) {
  // Get the current entry's level, style, and size
  const { entryNumIdx, titleIdx, levelIdx, styleIdx, sizeIdx, choreoIdx } = colIndexes;
  const { artisticIdx, technicalIdx } = judgesColIndexes;
  const row = scoreData[rowIndex] || [];
  const currLevel = row[levelIdx] || "";
  const currStyle = row[styleIdx] || "";
  const currSize = row[sizeIdx] || "";
  const currChoreo = row[choreoIdx] || "";
  const userEmail = user?.email || "";

  const [sortOrder, setSortOrder] = useState('numerical'); // default sort by entry number

  // Get Scores when a user, rowIndex, or judgeResponses change (e.g. after a new score submission) - ensures sidebar updates immediately after scoring without needing to refetch all scores
  const userScores = useMemo(() => {
    if (!userEmail || !judgeResponses || judgeResponses.length === 0) return {};
    // Skip header row if present
    const dataRows = judgeResponses.length > 0 && (judgeResponses[0][0].toLowerCase() === 'email') ? judgeResponses.slice(1) : judgeResponses;

    // Gets all the current user's scores keyed by entry number, with values as { artistic, technical }
    //Assumes responses tab has columns: Email[0] | EntryNum[1] | Artistic Score[2] | Technical Score[3] (with or without header row)
    //TODO - consider refactoring to use column indexes instead of hardcoded positions for email/entryNum/artistic/technical
    const scores = {};
    dataRows.forEach(row => {
      // Ensure values are numbers if possible, otherwise dash
      const artistic = row[artisticIdx] && !isNaN(row[artisticIdx]) ? Number(row[artisticIdx]) : (row[artisticIdx] || "-");
      const technical = row[technicalIdx] && !isNaN(row[technicalIdx]) ? Number(row[technicalIdx]) : (row[technicalIdx] || "-");
      scores[row[1]] = {
        artistic,
        technical
      };
    });
    return scores;
  }, [rowIndex, judgeResponses]);

  // Find all entries with the same level, style, and size from scoreData (skip header row)
  //Put Teacher choreographed dance in their own category.
  const filteredEntries = useMemo(() => {
    if (!scoreData.length) return [];
    const isTeacher = row && row[choreoIdx]?.toLowerCase() === 'teacher';
    return scoreData.slice(1).filter(r =>
      r[levelIdx] === currLevel &&
      r[styleIdx] === currStyle &&
      r[sizeIdx] === currSize &&
      (isTeacher
        ? r[choreoIdx].toLowerCase() === 'teacher'
        : r[choreoIdx].toLowerCase() !== 'teacher')
    );
  }, [scoreData, currLevel, currStyle, currSize, colIndexes]);


  // Find all user scores for entries in this category (by entry number)
  const filteredScores = useMemo(() => {
    const scores = {};
    filteredEntries.forEach(r => {
      const entryNum = r[entryNumIdx];
      let scoreObj = userScores[entryNum] || { artistic: "-", technical: "-" };
      // Format scores to 2 decimal places if they are numbers
      const formatScore = (val) => {
        if (val === undefined || val === null || val === "") return "-";
        if (!isNaN(val) && val !== "-") return Number(val).toFixed(2);
        return val;
      };
      scores[entryNum] = {
        artistic: formatScore(scoreObj.artistic),
        technical: formatScore(scoreObj.technical)
      };
    });
    return scores;
  }, [filteredEntries, userScores]);

  // Sort filteredEntries based on the selected sort order
  const sortedEntries = useMemo(() => {
    if (sortOrder === 'numerical') return filteredEntries;

    return [...filteredEntries].sort((a, b) => {
      const getScore = (r) => {
        const s = filteredScores[r[entryNumIdx]];
        if (!s) return -1;
        const art = parseFloat(s.artistic);
        const tech = parseFloat(s.technical);
        if (isNaN(art) && isNaN(tech)) return -1; // no scores

        return (art + tech) / 2; // simple avg of artistic and technical for sorting
      };
      return getScore(b) - getScore(a); // sort descending by score
    });
  }, [filteredEntries, filteredScores, sortOrder]);

  return (
    <div className="ossdf-sidebar">
      <Typography variant="h1" color="primary" sx={{ marginBottom: 0.5 }}>Entries in this Category ({sortedEntries.length})</Typography>
      <div className="ossdf-sort-toggle">
        Sort by:
        <button className={`ossdf-sort-btn${sortOrder === 'numerical' ? ' active' : ''}`}
          onClick={() => setSortOrder('numerical')}
          style={{ fontWeight: sortOrder === 'numerical' ? 'bold' : 'normal' }}
        >
          Entry
        </button>
        <button className={`ossdf-sort-btn${sortOrder === 'score' ? ' active' : ''}`}
          onClick={() => setSortOrder('score')}
          style={{ fontWeight: sortOrder === 'score' ? 'bold' : 'normal' }}
        >
          Score
        </button>
      </div>
      <div className="ossdf-sidebar-list">
        {sortedEntries.map((r, i) => {
          const entryNum = r[entryNumIdx];
          const scoreObj = filteredScores[entryNum] || { artistic: "-", technical: "-" };
          const isCurrent = scoreData[rowIndex]?.[entryNumIdx] === entryNum;

          // Find the index in the full scoreData for this entryNum
          const mainIndex = scoreData.findIndex(row => row[0] === entryNum);
          return (

            <span className="ossdf-awards-entry-line" key={entryNum ?? i}>
              <div
                className={`ossdf-sidebar-row${isCurrent ? " current" : ""}`}
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  if (onSelectEntry && mainIndex !== -1) onSelectEntry(mainIndex);
                }}
              >
                <span className="ossdf-sidebar-entry">#{entryNum}   {r[titleIdx] && (
                  <span className="ossdf-sidebar-entry">{r[titleIdx]}</span>
                )}</span>
                <Typography variant="numbers" color="text" sx={{ minWidth: 80, textAlign: 'right' }}>
                  <div> A:{scoreObj.artistic} </div>
                  <div> T:{scoreObj.technical} </div>
                </Typography>
              </div>
            </span>
          );
        })}
      </div>
    </div>
  );
}

export default CurrentCategorySidebar;
